import { ActionIcon } from '@mantine/core';
import { memo, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { toastNotifications } from 'shared/utils/toastNotifications';

interface PanelPlacementButtonProps {
    isAutoFormworkEnabledRef: RefObject<boolean>;
    placementButtonRef: RefObject<HTMLButtonElement | null>;
    articleDataLoaded: boolean;
    isAutomatedPanelPlacementLoading: boolean;
    handlePanelPlacementStart: () => void;
    updateActionIcon: () => void;
}

export const PanelPlacementButton = memo(
    ({
        isAutoFormworkEnabledRef,
        placementButtonRef,
        articleDataLoaded,
        isAutomatedPanelPlacementLoading,
        handlePanelPlacementStart,
        updateActionIcon
    }: PanelPlacementButtonProps) => {
        const { t } = useTranslation();

        const handlePanelPlacementButtonRef = (actionIcon: HTMLButtonElement | null) => {
            if (placementButtonRef.current === null && actionIcon) {
                placementButtonRef.current = actionIcon;
            }

            updateActionIcon();
        };

        const handleOnPointerDown = () => {
            if (isAutomatedPanelPlacementLoading) {
                return;
            }

            if (isAutoFormworkEnabledRef.current) {
                handlePanelPlacementStart();
            } else {
                toastNotifications.information({
                    title: t('info'),
                    message: articleDataLoaded ? t('infoAutoPanelPlacement') : t('labelPlacePanelsDisabled')
                });
            }
        };

        return (
            <ActionIcon
                ref={handlePanelPlacementButtonRef}
                aria-busy={isAutomatedPanelPlacementLoading}
                aria-label={t('labelPlacePanels')}
                className="pointer-events-auto action-icon-medium automated-placement-action-icon max-[1440px]:h-7.5 max-[1440px]:w-7.5 max-[1440px]:text-icon-sm max-[850px]:h-7.5 max-[850px]:w-7.5 max-[850px]:text-icon-sm"
                disabled={isAutomatedPanelPlacementLoading}
                variant="activated"
                onPointerDown={handleOnPointerDown}
            >
                {isAutomatedPanelPlacementLoading ? 'hourglass_top' : 'auto_fix'}
            </ActionIcon>
        );
    }
);

PanelPlacementButton.displayName = 'PanelPlacementButton';
